import React, { useEffect, useState, memo, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  addToWishlist,
  getProductInventory,
} from "../../Redux/AllSlices/products";
import { isAuthenticated } from "../../utils/auth";
import { toast } from "react-toastify";

function ProductCardComponent({ product, BaseUrl }) {
  const { wishlist } = useSelector((state) => state.products);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { profileData } = useSelector((state) => state.auth);

  // Synchronously compute stock and pricing from the product object if available
  const productInfo = useMemo(() => {
    if (!product) {
      return {
        isOutOfStock: true,
        price: null,
        mrp: null,
        discount: null,
        variationTitle: null,
        hasDirectData: false,
      };
    }

    const inventories = Array.isArray(product.inventories)
      ? product.inventories
      : null;
    const hasInventoriesArray = inventories !== null;

    // Check if direct pricing/inventory/stock exists on product
    const hasDirectData =
      hasInventoriesArray ||
      product.stock !== undefined ||
      product.price !== undefined ||
      product.selling_price !== undefined ||
      product.current_price !== undefined;

    let isOutOfStock = false;

    if (hasInventoriesArray) {
      if (inventories.length === 0) {
        // No inventories -> Out of Stock
        isOutOfStock = true;
      } else {
        const totalStock =
          product?.stock?.total_stock !== undefined
            ? Number(product.stock.total_stock)
            : null;
        const stockInStock =
          product?.stock?.in_stock !== undefined
            ? Boolean(product.stock.in_stock)
            : null;

        const hasAnyInStockInventory = inventories.some((inv) => {
          const inStockVal = inv?.in_stock;
          const isInStock =
            inStockVal === true || inStockVal === 1 || inStockVal === "1";
          const qty = Number(inv?.stock_quantity ?? 0);
          return isInStock && qty > 0;
        });

        if (
          (totalStock !== null && totalStock <= 0) ||
          stockInStock === false ||
          !hasAnyInStockInventory
        ) {
          isOutOfStock = true;
        }
      }
    } else if (product?.stock) {
      const totalStock =
        product.stock.total_stock !== undefined
          ? Number(product.stock.total_stock)
          : null;
      const stockInStock =
        product.stock.in_stock !== undefined
          ? Boolean(product.stock.in_stock)
          : null;
      if ((totalStock !== null && totalStock <= 0) || stockInStock === false) {
        isOutOfStock = true;
      }
    } else if (
      product?.stock_status === "0" ||
      product?.stock_quantity === "0" ||
      product?.stock_quantity === 0
    ) {
      isOutOfStock = true;
    }

    // First inventory resolution (prioritizing in-stock inventory)
    const firstInStockInv = inventories?.find((inv) => {
      const inStockVal = inv?.in_stock;
      const isInStock =
        inStockVal === true || inStockVal === 1 || inStockVal === "1";
      const qty = Number(inv?.stock_quantity ?? 0);
      return isInStock && qty > 0;
    });
    const firstInv = firstInStockInv || inventories?.[0];

    const rawPrice =
      firstInv?.selling_price ??
      product?.price?.selling_price ??
      product?.selling_price ??
      product?.current_price ??
      product?.mrp;

    const rawMrp =
      firstInv?.mrp ??
      product?.price?.mrp ??
      product?.mrp ??
      product?.current_mrp;

    const rawDiscount =
      firstInv?.discount ??
      product?.price?.discount ??
      product?.discount_percent ??
      product?.discount ??
      product?.current_discount;

    const price =
      rawPrice !== null &&
      rawPrice !== undefined &&
      rawPrice !== "" &&
      !isNaN(Number(rawPrice))
        ? Number(rawPrice)
        : null;

    const mrp =
      rawMrp !== null &&
      rawMrp !== undefined &&
      rawMrp !== "" &&
      !isNaN(Number(rawMrp))
        ? Number(rawMrp)
        : null;

    const discount =
      rawDiscount !== null &&
      rawDiscount !== undefined &&
      rawDiscount !== "" &&
      !isNaN(Number(rawDiscount))
        ? Math.round(Number(rawDiscount))
        : null;

    const variationTitle = firstInv?.variations?.title || null;

    // If price is missing or 0, treat as out of stock
    if (price === null || price <= 0) {
      isOutOfStock = true;
    }

    return {
      isOutOfStock,
      price,
      mrp,
      discount,
      variationTitle,
      hasDirectData,
    };
  }, [product]);

  const [asyncState, setAsyncState] = useState({
    price: null,
    mrp: null,
    discount: null,
    isOutOfStock: null,
  });

  // Fallback: only fetch inventory if product does NOT have direct inventory/pricing info
  useEffect(() => {
    let isMounted = true;
    if (productInfo.hasDirectData || !product?.id) {
      return;
    }

    dispatch(getProductInventory({ product_id: product.id }))
      .unwrap()
      .then((res) => {
        if (!isMounted) return;
        const hasValidPrice =
          res?.current_price !== undefined &&
          res?.current_price !== null &&
          Number(res?.current_price) > 0;

        if (hasValidPrice) {
          setAsyncState({
            price: Number(res.current_price),
            mrp:
              res?.current_mrp !== undefined && res?.current_mrp !== null
                ? Number(res.current_mrp)
                : null,
            discount:
              res?.current_discount !== undefined &&
              res?.current_discount !== null
                ? Math.round(Number(res.current_discount))
                : null,
            isOutOfStock: false,
          });
        } else {
          setAsyncState({
            price: null,
            mrp: null,
            discount: null,
            isOutOfStock: true,
          });
        }
      })
      .catch(() => {
        if (isMounted) {
          setAsyncState({
            price: null,
            mrp: null,
            discount: null,
            isOutOfStock: true,
          });
        }
      });

    return () => {
      isMounted = false;
    };
  }, [product?.id, productInfo.hasDirectData, dispatch]);

  const isOutOfStock = productInfo.hasDirectData
    ? productInfo.isOutOfStock
    : asyncState.isOutOfStock ?? true;

  const currentPrice = productInfo.hasDirectData
    ? productInfo.price
    : asyncState.price;

  const currentMrp = productInfo.hasDirectData
    ? productInfo.mrp
    : asyncState.mrp;

  const currentDiscount = productInfo.hasDirectData
    ? productInfo.discount
    : asyncState.discount;

  const variationTitle = productInfo.variationTitle;

  const handleNavigate = () => {
    const targetSlug = product?.slug || product?.id;
    if (targetSlug) {
      navigate(`/product-details/${targetSlug}`);
    }
  };

  useEffect(() => {
    if (Array.isArray(wishlist) && wishlist.length > 0 && product?.id) {
      const isInWishlist = wishlist.some(
        (item) => item?.product_id === product.id
      );
      setIsWishlisted(isInWishlist);
    } else {
      setIsWishlisted(false);
    }
  }, [wishlist, product?.id]);

  const handleAddToWishList = (e) => {
    e.stopPropagation();
    if (!isAuthenticated()) {
      toast.info("Please login to add to wishlist");
      navigate("/login");
      return;
    }

    const wishlistData = {
      user_id: profileData?.id,
      product_id: product?.id,
    };

    dispatch(addToWishlist(wishlistData))
      .unwrap()
      .then((res) => {
        if (res?.status === "success") {
          toast.success("Product added to wishlist successfully!");
          setIsWishlisted(true);
        }
        if (res?.status === "removed") {
          toast.success(res?.message || "Removed from wishlist");
          setIsWishlisted(false);
        }
      })
      .catch(() => {
        toast.error("Failed to update wishlist!");
      });
  };

  return (
    <div
      className="group relative bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100  cursor-pointer flex flex-col h-full"
      onClick={handleNavigate}
    >
      {/* Stock / Discount Badges */}
      <div className="absolute top-3 left-3 z-10 flex flex-col gap-1">
        {isOutOfStock ? (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold bg-red-600 text-white shadow-md tracking-wider uppercase">
            Out of Stock
          </span>
        ) : (
          currentDiscount &&
          currentDiscount > 0 && (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-md">
              {currentDiscount}% OFF
            </span>
          )
        )}
      </div>

      {/* Wishlist Button */}
      <button
        onClick={handleAddToWishList}
        className="absolute top-3 right-3 z-10 p-2 rounded-full bg-white/90 backdrop-blur-sm border border-gray-200 shadow-md hover:shadow-lg hover:scale-110 transition-all duration-200"
        aria-label="Add to wishlist"
      >
        <svg
          className={`w-5 h-5 transition-colors duration-200 ${
            isWishlisted ? "text-red-500" : "text-gray-400"
          }`}
          fill={isWishlisted ? "currentColor" : "none"}
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
          />
        </svg>
      </button>

      {/* Product Image */}
      <div className="relative flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden aspect-square">
        <div className="relative w-full h-full flex items-center justify-center ">
          {product?.main_image ? (
            <img
              src={`${BaseUrl || process.env.REACT_APP_BASE_URL || ""}/${
                product?.main_image
              }`}
              alt={product?.title || "Product"}
              loading="lazy"
              decoding="async"
              className={`max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-300 ${
                isOutOfStock ? "opacity-80" : ""
              }`}
            />
          ) : (
            <div className="bg-gray-200 border-2 border-dashed rounded-xl w-full h-full flex items-center justify-center">
              <span className="text-gray-500 text-xs">Image not available</span>
            </div>
          )}
        </div>
      </div>

      {/* Product Info */}
      <div className="p-4 space-y-3 flex flex-col flex-1 justify-between">
        <div>
          {/* Product Title */}
          <h3 className="text-sm font-semibold text-gray-800 line-clamp-2 min-h-[2.5rem] leading-tight cursor-pointer hover:text-[#482607] transition-colors">
            {product?.title}
          </h3>

          {/* Price Section */}
          <div className="flex items-center gap-2 flex-wrap min-h-[1.75rem] mt-2">
            {isOutOfStock ? (
              <span className="text-sm font-semibold text-red-500">
                Out of Stock
              </span>
            ) : (
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-xl font-bold text-gray-900">
                  ₹{currentPrice}
                </span>
                {currentMrp && Number(currentMrp) > Number(currentPrice) && (
                  <span className="text-sm text-gray-500 line-through">
                    ₹{currentMrp}
                  </span>
                )}
                {variationTitle && (
                  <span className="text-xs text-gray-500 font-medium">
                    ({variationTitle})
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Action Button */}
        <button
          onClick={handleNavigate}
          className="w-full font-semibold py-2 px-2 transition-all duration-200 shadow-md rounded-lg text-sm bg-[#77c712] hover:bg-[#68ad10] text-white hover:shadow-lg active:scale-95"
        >
          View Details
        </button>
      </div>
    </div>
  );
}

export const ProductCard = memo(ProductCardComponent);
export default ProductCard;